/**
 * useSeleccionMapaTabla — selección espacial compartida mapa↔tabla (Bloque P · S.UI.P5).
 *
 * Primitiva con estado del espacio map-céntrico. Posee la selección compartida
 * (un único `idSeleccion`) que se cablea de forma controlada a `AttributeTable`
 * (`idSeleccion`/`onSeleccionar`) y al mapa (resaltado/clic de feature), logrando
 * la selección bidireccional (principio de diseño 2). Añade «click-to-set»
 * (principio 7): para un registro armado (seleccionado y fijable), un clic en el
 * mapa fija su coordenada reproyectando WGS84→EPSG:25830.
 *
 * Sin lógica de negocio: no geocodifica, no valida y no persiste. La escritura
 * real del registro la hace el consumidor (montaje A3b). El predicado `puedeFijar`
 * lo inyecta el consumidor, de modo que el hook es agnóstico del modelo de datos.
 *
 * Convenciones calcadas del código vivo:
 * - Selección TOGGLE: re-seleccionar el id activo lo deselecciona (→ null),
 *   igual que `CandidatesWithMap` (`handleSelectFromList`/`handleSelectFromMap`).
 * - Coordenada emitida en floats sin redondear, igual que `onDragEditCoords`
 *   (`toUTM30` crudo); el redondeo es solo de presentación (convención QGIS).
 */
import { useCallback, useMemo, useState } from 'react';

import { pointToUTM30 } from '../../../lib/leaflet-crs';
import type { Coord25830 } from './StatusBar';

/** Punto geográfico WGS84 (latitud/longitud), tal como lo emite Leaflet. */
export interface LatLng {
  lat: number;
  lng: number;
}

export interface UseSeleccionMapaTablaParams {
  /** Selección inicial (solo en el primer render). Def: null. */
  idSeleccionInicial?: string | null;
  /**
   * Predicado de fijabilidad: ¿este id admite click-to-set? (típicamente,
   * registro «pendiente» sin coordenada). Inyectado por el consumidor para no
   * acoplar el hook al modelo de datos. Def: () => false.
   */
  puedeFijar?: (id: string) => boolean;
  /** Se invoca al fijar coordenada por clic en el mapa (EPSG:25830, floats). */
  onFijarCoord?: (id: string, coord: Coord25830) => void;
  /** Se invoca cuando cambia la selección compartida. */
  onSeleccionarCambio?: (id: string | null) => void;
}

export interface UseSeleccionMapaTablaResult {
  /** Selección compartida actual (cablear a la tabla como `idSeleccion ?? undefined`). */
  idSeleccion: string | null;
  /** Selecciona/deselecciona (TOGGLE). Cablear a tabla y mapa por igual. */
  seleccionar: (id: string) => void;
  /** Id armado para click-to-set: `idSeleccion` sii `puedeFijar(idSeleccion)`; si no, null. */
  idArmadoClickSet: string | null;
  /** Maneja un clic en el mapa: si hay registro armado, fija su coord (WGS84→25830). */
  manejarClicMapa: (latlng: LatLng) => void;
}

/** Hook de selección espacial compartida + click-to-set (S.UI.P5). */
export function useSeleccionMapaTabla(
  params: UseSeleccionMapaTablaParams = {},
): UseSeleccionMapaTablaResult {
  const { idSeleccionInicial = null, puedeFijar, onFijarCoord, onSeleccionarCambio } = params;

  const [idSeleccion, setIdSeleccion] = useState<string | null>(idSeleccionInicial);

  // TOGGLE: re-seleccionar el id activo lo deselecciona (calca CandidatesWithMap).
  const seleccionar = useCallback(
    (id: string) => {
      const siguiente = id === idSeleccion ? null : id;
      setIdSeleccion(siguiente);
      onSeleccionarCambio?.(siguiente);
    },
    [idSeleccion, onSeleccionarCambio],
  );

  // Armado derivado: solo el id seleccionado y fijable admite click-to-set.
  const idArmadoClickSet = useMemo<string | null>(() => {
    if (idSeleccion == null) return null;
    return puedeFijar?.(idSeleccion) ? idSeleccion : null;
  }, [idSeleccion, puedeFijar]);

  // Click-to-set: no-op si no hay armado; si lo hay, reproyecta y emite 25830 (floats).
  const manejarClicMapa = useCallback(
    (latlng: LatLng) => {
      if (idArmadoClickSet == null) return;
      const coord = pointToUTM30(latlng);
      onFijarCoord?.(idArmadoClickSet, coord);
    },
    [idArmadoClickSet, onFijarCoord],
  );

  return { idSeleccion, seleccionar, idArmadoClickSet, manejarClicMapa };
}
